'use client';

import { CompositeItem } from '@floating-ui/react';
import { forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { photoCarouselDotRecipe } from './styles/photo-carousel-dot.styles';
import type { PhotoCarouselDotPropsInterface } from './types/photo-carousel-dot.types';

/**
 * Used by `PhotoCarousel.Dots` to render an individual dot.
 * Based on a non-interactive `tab` element.
 *
 * @since 8.66.0
 */

export const PhotoCarouselDot = forwardRef<HTMLButtonElement, PhotoCarouselDotPropsInterface>(
  (props: PhotoCarouselDotPropsInterface, forwardedRef) => {
    const {
      'aria-controls': ariaControls,
      'aria-label': ariaLabel,
      'aria-selected': ariaSelected,
      className,
      css: cssProp,
      isContinuation = false,
      isHidden = false,
      ...rest
    } = props;

    const styles = css(
      photoCarouselDotRecipe.raw({ selected: !!ariaSelected, isContinuation, isHidden }),
      cssProp,
    );

    return (
      <CompositeItem
        render={
          <button
            aria-controls={ariaControls}
            aria-label={ariaLabel}
            aria-selected={ariaSelected}
            className={cx('c11n', 'c11n_photo-carousel__dot', styles, className)}
            data-c11n-component="PhotoCarousel.Dot"
            data-c11n-version={__C11N_VERSION__}
            ref={forwardedRef}
            role="tab"
            type="button"
            {...rest}
          />
        }
      />
    );
  },
);

PhotoCarouselDot.displayName = 'PhotoCarousel.Dot';
